// Workspace URL scoping.
//
// The active workspace is the FIRST path segment: app.hq.zone/<slug>/… (the
// GitHub / Linear / Vercel shape). vue-router is created with `/<slug>/` as its
// history BASE (see router/index.ts), so the prefix is transparently stripped
// from every incoming route and re-added to every link / push - existing route
// definitions, names, `<router-link>`s, and `router.push` calls are all
// UNCHANGED. Because the base is fixed for a page load, the active workspace is
// constant within a tab; switching is a full navigation into the new `/<slug>/`
// space. Two tabs can therefore show two workspaces at once, each authorized by
// the one shared email-keyed session cookie - the server validates the slug
// against the caller's memberships
// (crates/hq-controlplane/src/auth_session.rs::resolve_cookie_workspace).
//
// Since the slug sits at the root, it must be told apart from a public route or
// an infra path. RESERVED_ROOT is that set: a first segment in it is NOT a
// workspace. A workspace handle can never BE one of these - the server rejects
// reserved slugs (email_slug_reserved, migration 0187) and scripts/
// check-reserved.mjs fails the build if a public route's segment isn't listed
// here. Keep this in lockstep with that migration.
//
// API calls carry the active workspace as the `X-Hq-Ws` header (see
// api/client.ts); direct navigations that can't set a header (downloads, image
// embeds) carry it as a `?ws=<slug>` query hint instead.

/** True-root path segments that are NOT workspaces: public/auth SPA routes +
 *  nginx-intercepted infra (API, install, static). */
export const RESERVED_ROOT: ReadonlySet<string> = new Set([
  // public / auth SPA routes (served at the true root)
  'signin', 'oauth', 'auth', 'signup', 'get-started', 'dev',
  // nginx-intercepted infra on app.hq.zone (proxied / static, never the SPA)
  'v1', 'slack', 'conversations', 'assets', 'metrics', 'readyz', 'healthz',
]);

// Tenant slugs: lowercase, start alnum, `[a-z0-9-]` (migration 0186 CHECK).
const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

/** The workspace slug at the front of an origin-relative path (the first
 *  segment), applying the reserved-root + slug-shape rules. `null` when the
 *  first segment isn't a workspace. Tolerates a leading query string. Shared by
 *  `currentWorkspace` and callers that need the workspace of some OTHER path
 *  (e.g. parsing `?next=/hq/…` on the sign-in screen). */
export function workspaceFromPath(path: string): string | null {
  const seg = (path.split(/[?#]/)[0] ?? '').split('/')[1] ?? '';
  if (!seg || RESERVED_ROOT.has(seg) || !SLUG_RE.test(seg)) return null;
  return seg;
}

/** The workspace slug from the current URL, or `null` on a workspace-less
 *  (public / infra / pre-resolution) path. The slug is the first path segment,
 *  unless it's reserved or not slug-shaped. */
export function currentWorkspace(): string | null {
  return workspaceFromPath(window.location.pathname);
}

/** vue-router history base for this page load: `/<slug>/` when the URL is
 *  workspace-scoped, else `/`. Computed once, at router creation. */
export function historyBase(): string {
  const ws = currentWorkspace();
  return ws ? `/${ws}/` : '/';
}

/** An origin-relative path into a workspace's URL space, preserving an in-app
 *  sub-path (which may carry a query string). Used to switch workspaces and to
 *  self-heal a workspace-less / wrong-workspace URL via a full navigation. */
export function workspacePath(slug: string, subPath = '/'): string {
  let sub = subPath || '/';
  if (!sub.startsWith('/')) sub = `/${sub}`;
  return `/${slug}${sub}`;
}

/** Annotate a same-origin API URL with the active workspace as a `?ws=<slug>`
 *  hint, for DIRECT navigations (`<img src>`, download `<a href>`) that can't
 *  set the `X-Hq-Ws` header. The controlplane reads `?ws` as an alternative to
 *  the header and validates it against memberships exactly the same way. No-op
 *  when workspace-less or for non-`/v1/` (external/absolute) URLs. */
export function wsUrl<T extends string | null | undefined>(url: T): T {
  const ws = currentWorkspace();
  if (!url || !ws || !url.startsWith('/v1/')) return url;
  if (/[?&]ws=/.test(url)) return url; // idempotent: safe to apply at >1 layer
  return (url + (url.includes('?') ? '&' : '?') + 'ws=' + encodeURIComponent(ws)) as T;
}
